/**
 * APTextField Component
 *
 * A comprehensive text field component following Figma design system specifications.
 * Supports multiple sizes, statuses, icons, and accessibility features.
 *
 * @example
 * ```tsx
 * <APTextField
 *   label="Email"
 *   placeholder="Enter your email"
 *   hint="We'll never share your email"
 *   size="large"
 *   status="default"
 * />
 *
 * <APTextField
 *   label="Amount"
 *   labelExtra="Available: 4,830 USD"
 *   placeholder="0.00"
 *   status="focus"
 *   clearable
 *   rightIcon={<DollarIcon />}
 * />
 * ```
 */

import { forwardRef, useCallback, useId, useState } from 'react';

import APTypography from '@shared/components/atoms/typography';
import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPTextFieldProps } from './types';

import './styles.scss';

import { APIconButton } from '@shared/components';

/**
 * APTextField - Production-ready text input component
 *
 * Follows AstroPay design system and accessibility guidelines.
 *
 */
export const APTextField = forwardRef<HTMLInputElement, IAPTextFieldProps>(
  (
    {
      size = 'medium',
      status = 'default',
      label,
      hint,
      labelExtra,
      leftIcon,
      rightIcon,
      clearable = false,
      required = false,
      fullWidth = true,
      className = '',
      'data-testid': testId,
      errorMessage,
      successMessage,
      onClear,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      disabled,
      ...rest
    },
    ref
  ) => {
    const translate = useTranslate();
    // Generate unique id for proper label association
    const generatedId = useId();
    const inputId = rest.id || generatedId;

    // Internal state for focus management
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(value || defaultValue || '');

    // Determine actual status based on props and state
    const getActualStatus = () => {
      if (disabled) return 'disabled';
      if (status === 'error' || status === 'success') return status;
      if (isFocused) return 'focus';
      if (internalValue) return 'filled';
      return 'default';
    };

    const actualStatus = getActualStatus();

    // Determine if clear button should be shown
    const showClearButton = clearable && internalValue && !disabled && actualStatus !== 'disabled';

    // Event handlers
    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus?.(e);
      },
      [onFocus]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur?.(e);
      },
      [onBlur]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        onChange?.(e);
      },
      [onChange]
    );

    const handleClear = useCallback(() => {
      setInternalValue('');
      onClear?.();
    }, [onClear]);

    // Construct class names using BEM methodology
    const baseClasses = 'ap-textfield';
    const sizeClasses = `ap-textfield--${size}`;
    const statusClasses = `ap-textfield--${actualStatus}`;
    const stateClasses = [fullWidth && 'ap-textfield--full-width'].filter(Boolean);

    const textFieldClasses = [baseClasses, sizeClasses, statusClasses, ...stateClasses, className]
      .filter(Boolean)
      .join(' ');

    // Determine hint message based on status
    const getHintMessage = () => {
      if (actualStatus === 'error' && errorMessage) return errorMessage;
      if (actualStatus === 'success' && successMessage) return successMessage;
      return hint;
    };

    const hintMessage = getHintMessage();

    return (
      <div className={textFieldClasses} data-testid={testId}>
        {/* Label row */}
        {(label || labelExtra) && (
          <div className="ap-textfield__label-row">
            {label && (
              <APTypography
                as="label"
                size="small"
                weight="500"
                color="base"
                className="ap-textfield__label"
                {...{ htmlFor: inputId }}
              >
                {label}
                {required && <APTypography as="span" color="error" text="*" inline />}
              </APTypography>
            )}
            {labelExtra && (
              <APTypography
                as="span"
                size="small"
                weight="400"
                color="muted"
                className="ap-textfield__label-extra"
              >
                {labelExtra}
              </APTypography>
            )}
          </div>
        )}

        {/* Input container */}
        <div className="ap-textfield__input-container">
          {/* Left icon */}
          {leftIcon && <div className="ap-textfield__left-icon">{leftIcon}</div>}

          {/* Input wrapper for cursor and text */}
          <div className="ap-textfield__input-wrapper">
            <input
              ref={ref}
              type="text"
              className="ap-textfield__input"
              id={inputId}
              value={value}
              defaultValue={defaultValue}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              required={required}
              aria-invalid={actualStatus === 'error'}
              aria-describedby={hintMessage ? `${inputId}-hint` : undefined}
              aria-required={required}
              {...rest}
            />
          </div>

          {/* Right container for icons and clear button */}
          <div className="ap-textfield__right-container">
            {/* Clear button */}
            {showClearButton && (
              <APIconButton
                icon="close_line"
                size={'small'}
                type="button"
                variant="transparent"
                className="ap-textfield__clear-button"
                onClick={handleClear}
                aria-label={translate('aria.clear-field')}
              />
            )}

            {/* Right icon */}
            {rightIcon && <div className="ap-textfield__right-icon">{rightIcon}</div>}
          </div>
        </div>

        {/* Hint message */}
        {hintMessage && (
          <APTypography
            as="p"
            size="xSmall"
            weight="400"
            color={
              actualStatus === 'error' ? 'error' : actualStatus === 'success' ? 'success' : 'muted'
            }
            className="ap-textfield__hint"
            id={`${inputId}-hint`}
          >
            {hintMessage}
          </APTypography>
        )}
      </div>
    );
  }
);

APTextField.displayName = 'APTextField';

export type { IAPTextFieldProps };
export default APTextField;
